import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { hydrateAttribution } from './attribution';
import App from './App';
import ResultsPage from './ResultsPage';
import SampleResultPage from './SampleResultPage';
import AnswersPage from './AnswersPage';
import CheckoutPage from './CheckoutPage';
import BookingPage from './BookingPage';
import BookingConfirmedPage from './BookingConfirmedPage';
import BookingManagePage from './BookingManagePage';
import LetsTalkPage from './LetsTalkPage';
import { WelcomePage } from './WelcomePage';
import { TransferFirmPage } from './TransferFirmPage';
import { PracticeSalePage } from './PracticeSalePage';
import { PracticeSaleBuyPage } from './PracticeSaleBuyPage';
import ProgramSalesPage from './ProgramSalesPage';
import ProgramOrderPage from './ProgramOrderPage';
import {
  ProgramThanksPage,
  ProgramOnboardingPage,
  ProgramSummaryPage,
} from './ProgramPostPurchasePages';
import CohortPage from './CohortPage';
import PreferenceCenterPage from './PreferenceCenterPage';
import ReplayPage from './ReplayPage';
import WaitlistPage from './WaitlistPage';
import BkosFoundingCohortPage from './BkosFoundingCohortPage';
import CallLandingPage from './CallLandingPage';
import CallAttendPage from './CallAttendPage';
import CallReplayPage from './CallReplayPage';
import IssueFeedbackPage from './IssueFeedbackPage';
import FreeCoursePage from './FreeCoursePage';
import ClaimSeatPage from './ClaimSeatPage';
import { GiftClaimPage, GiftConfirmPage } from './GiftPage';
import StartNowPage from './StartNowPage';
import CallAskPage from './CallAskPage';
import CallRegisteredPage from './CallRegisteredPage';
import EventDoorPage from './EventDoorPage';
import EventPurchasedPage from './EventPurchasedPage';
import SessionDoorPage from './SessionDoorPage';
import MagnetPage from './MagnetPage';
import MagnetAccessPage from './MagnetAccessPage';
import MagnetLibraryPage from './MagnetLibraryPage';
import WebinarPage from './WebinarPage';
import WebinarReplayPage from './WebinarReplayPage';
import SmsOptInPage from './SmsOptInPage';
import { CancelStartPage, CancelRequestPage, CancelWithdrawPage } from './CancelPage';
import BkosMoreClientsPage, { BkosMoreClientsThanksPage } from './BkosMoreClientsPage';
import BkosApprovePage from './BkosApprovePage';
import UpgradeCreditPage from './UpgradeCreditPage';
import QuestionFormPage from './QuestionFormPage';
import FreeBookPage from './FreeBookPage';
import { BookAddressPage } from './BookAddressPage';
import { BookArrivedPage } from './BookArrivedPage';
import { BkosRatePage } from './BkosRatePage';
import { RateEventPage, RateConfirmPage } from './RateEventPage';
import { StillReadingPage } from './StillReadingPage';
import { SummitNotThisYearPage } from './SummitNotThisYearPage';
import { OfferChooserPage } from './OfferChooserPage';
import { SharePage } from './SharePage';
import TestimonialPage from './TestimonialPage';
import CheckinPage from './CheckinPage';
import BrokenPage from './components/BrokenPage';
import VersionBadge from './VersionBadge';
import './styles.css';
import './blocks.css';
import './blocks-events.css';
import './sections.css';

// ─── TAKER INFRASTRUCTURE CONTRACT ──────────────────────────────────────────
// Every page added here MUST use one of the two approved infrastructure paths:
//
//   A) TakerShell (components/TakerShell.tsx)
//      → For standalone marketing/info pages (calls, programs, lets-talk, etc.)
//      → Provides: Fraunces+Inter fonts, TSB nav (3 destinations + CTA), footer.
//      → Reference: CallLandingPage, ProgramSalesPage (migrate when next touched)
//
//   B) SiteNav blocks from config (type: 'siteNav' in assessment JSON)
//      → For config-driven event/assessment flows
//      → Reference: App.tsx (take/:slug, events/:slug), ReplayPage
//
// VersionBadge is global (rendered below) — visible at any URL with ?build or
// after localStorage.setItem('ee_build','1') in any browser. Never hide it behind
// a different mechanism.
// ─────────────────────────────────────────────────────────────────────────────

// Host-aware root. getbkos.com is retired (Michael, 2026-09-13 — all BKOS
// marketing content consolidates onto thesuccessfulbookkeeper.com/bkos, and
// every taker page other than booking/event pages runs under
// discover.thesuccessfulbookkeeper.com). Every other taker host has no
// bare-root page → send to the TSB marketing site. This used to be a
// "/ -> TSB" edge rule in _redirects, but that matches PATH ONLY and so
// redirected getbkos.com's front door away too. Doing it here keeps the
// redirect host-aware.
// The cancellation form lives on discover.thesuccessfulbookkeeper.com only.
// purebookkeeping's /cancel is the PBS licence exit (HubSpot) and must never be
// confused with this form (plan §14 item 8); the retired BKOS taker hosts
// forward to the TSB host with BKOS branding. Path matching in _redirects is
// host-blind, so this is done here, like RootRoute.
function CancelHostGate({ children }: { children: React.ReactNode }) {
  const host = window.location.hostname.toLowerCase();
  if (host === 'discover.purebookkeeping.com') {
    window.location.replace('https://www.purebookkeeping.com/cancel');
    return null;
  }
  if (host === 'discover.bkos.io' || host === 'getbkos.com' || host === 'www.getbkos.com') {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('for')) params.set('for', 'bkos');
    window.location.replace(`https://discover.thesuccessfulbookkeeper.com${window.location.pathname}?${params.toString()}`);
    return null;
  }
  return <>{children}</>;
}

function RootRoute() {
  const host = window.location.hostname.toLowerCase();
  // getbkos.com and discover.bkos.io are both retired BKOS-branded taker
  // surfaces — same treatment for both. (bkos.io itself, the real product
  // platform, is untouched by any of this; only the discover.* taker
  // subdomain is ours.)
  if (host === 'getbkos.com' || host === 'www.getbkos.com' || host === 'discover.bkos.io') {
    // Route through /programs/bkos (not the offsite URL directly) so the
    // actual destination stays written in exactly one place — the
    // /programs/bkos rule in taker/public/_redirects, same reasoning as the
    // retired BkosFoundingCohortPage routes below it in that file.
    window.location.replace('/programs/bkos');
    return null;
  }
  window.location.replace('https://www.thesuccessfulbookkeeper.com/');
  return null;
}

// /getbkos was the host-independent preview path for the retired BKOS front
// door — kept as a redirect rather than removed outright so an old bookmark
// or internal link lands somewhere real instead of a 404.
function RetiredGetbkosPage() {
  window.location.replace('/programs/bkos');
  return null;
}

// Attribution capture — mounted once at the router root so EVERY taker route
// hydrates UTM/click-ID cookies from the URL, not just the two that render
// <App> (/take/:slug, /events/:slug). Before 2026-09-18, hydrateAttribution()
// was only called inside App.tsx's own mount effect — every other taker page
// (webinars, program sales, calls, checkout, Freedom Gateway, BKOS Founding
// Cohort, the native booking picker...) called getAttributionPayload() on
// submit but had never hydrated the cookies it reads, so first/last-touch
// UTM, referrer and click-ID fields silently POSTed empty on every opt-in
// outside /take and /events. Root cause + full incident writeup: PR fixing
// this (search "attribution hydration" in git log). The effect fires on
// mount, well before any user-triggered submit can call
// getAttributionPayload(), so there is no ordering race with page-level
// submit handlers.
//
// Skips when `?preview=` is present — that's App.tsx's own builder-preview
// query param (admin's ChatDock/config preview links always point at
// /take/:slug or /events/:slug with ?preview=<phase>), and a preview load
// must never mint real attribution cookies for a phase that isn't a genuine
// visit. hydrateAttribution() itself is idempotent (never overwrites an
// existing first-touch cookie), so this component is safe to mount
// unconditionally otherwise.
function AttributionHydrator() {
  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).has('preview')) return;
    } catch { /* malformed search string — fall through and hydrate anyway */ }
    hydrateAttribution();
  }, []);
  return null;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <VersionBadge />
    <AttributionHydrator />
    <BrowserRouter>
      <Routes>
        {/* Root — host-aware. getbkos.com is retired and redirects to the TSB
            marketing site; other taker hosts have no "/" page (404 as before). */}
        <Route path="/" element={<RootRoute />} />
        <Route path="/getbkos" element={<RetiredGetbkosPage />} />
        {/* Path A: config-driven (siteNav blocks own the chrome) */}
        <Route path="/take/:slug" element={<App />} />
        <Route path="/events/:slug" element={<App />} />
        {/* Attendance identity gate door page — the personal-link check-in
            page registrants land on before Zoom when eventConfig.doorGate is
            on (default off). See worker/src/event-door-routes.ts. Registered
            here (not Path A) since it's a standalone TakerShell doorway, not
            a config-driven siteNav page. */}
        <Route path="/events/:slug/join" element={<EventDoorPage />} />
        {/* Spiffy thank-you target for paid event checkouts (Summit): waits for the order, then opens the buyer's own prep survey. */}
        <Route path="/events/:slug/thanks" element={<EventPurchasedPage />} />
        {/* Program-session attendance door — the session-runtime sibling of
            the above, live only for programs that opted into attendance
            capture (programs.attendance_capture_from set). See
            worker/src/session-door-routes.ts, Phase 4.A step 1. */}
        <Route path="/sessions/:id/join" element={<SessionDoorPage />} />
        {/* Path B: TakerShell (or pending migration — see contract above) */}
        {/* BKOS Founding Cohort — TakerShell (TSB Global chrome) wraps the
            BKOS-green body. Michael 2026-06-12: TSB drives BKOS adoption, so
            the TSB header/footer frame the page. */}
        <Route path="/programs/bkos-founding-cohort" element={<BkosFoundingCohortPage />} />
        {/* Australian region cohort — same page, pins registration to the AU
            cohort (multi-region routing, migration 0145). */}
        <Route path="/programs/bkos-founding-cohort/au" element={<BkosFoundingCohortPage region="au" />} />
        <Route path="/programs/:slug/order" element={<ProgramOrderPage />} />
        <Route path="/programs/:slug/thanks" element={<ProgramThanksPage />} />
        <Route path="/programs/:slug/onboarding" element={<ProgramOnboardingPage />} />
        <Route path="/programs/:slug/summary" element={<ProgramSummaryPage />} />
        <Route path="/programs/:slug" element={<ProgramSalesPage />} />
        {/* Region-aware sales page (migration 0163): /programs/:slug/:region pins
            the matching regional cohort (e.g. /programs/freedom-gateway/au). The
            literal order/thanks/onboarding/summary routes above outrank :region. */}
        <Route path="/programs/:slug/:region" element={<ProgramSalesPage />} />
        {/* Post-purchase landing for the 16 price-card checkout slugs (Spiffy thank-you target). */}
        <Route path="/welcome/:offerSku" element={<WelcomePage />} />
        {/* A BKOS transfer customer's firm details only (mct_b27e5971-bd1). */}
        <Route path="/welcome/:offerSku/firm" element={<TransferFirmPage />} />
        <Route path="/results/:submissionId" element={<ResultsPage />} />
        <Route path="/sample/:slug" element={<SampleResultPage />} />
        <Route path="/answers/:submissionId" element={<AnswersPage />} />
        <Route path="/checkout/:slug" element={<CheckoutPage />} />
        {/* Plan chooser for a time-boxed offer across several checkouts, e.g. the NA Summit BKOS booth (mct_5a7bac4e-a3b). */}
        <Route path="/offer/:key" element={<OfferChooserPage />} />
        <Route path="/book/:personSlug/:variantKey" element={<BookingPage />} />
        <Route path="/book/:personSlug" element={<BookingPage />} />
        <Route path="/booked/:slug" element={<BookingConfirmedPage />} />
        <Route path="/lets-talk/:programSlug" element={<LetsTalkPage />} />
        <Route path="/bookings/manage/:token" element={<BookingManagePage />} />
        <Route path="/cohort/:token" element={<CohortPage />} />
        <Route path="/preferences/:token" element={<PreferenceCenterPage />} />
        {/* One-click issue rating from a campaign email ({{issueFeedback}}). */}
        <Route path="/feedback/:rating" element={<IssueFeedbackPage />} />
        {/* Day-31 FRWY free course: the buyer picks a program (free-course-notices.ts). */}
        <Route path="/free-course" element={<FreeCoursePage />} />
        {/* Free tier: claim the free community seat (worker/src/free-tier.ts). 404s until intake is switched on. */}
        <Route path="/claim-your-seat" element={<ClaimSeatPage />} />
        <Route path="/gift/confirm" element={<GiftConfirmPage />} />
        <Route path="/gift/:campaign" element={<GiftClaimPage />} />
        {/* Start the free program now, giving up the money-back refund (money-back-waiver.ts). */}
        <Route path="/free-course/start-now" element={<StartNowPage />} />
        <Route path="/replays/:slug" element={<ReplayPage />} />
        <Route path="/waitlist/:slug" element={<WaitlistPage />} />
        <Route path="/calls/:callSlug" element={<CallLandingPage />} />
        <Route path="/calls/:callSlug/registered" element={<CallRegisteredPage />} />
        <Route path="/calls/:callSlug/attend/:value" element={<CallAttendPage />} />
        <Route path="/calls/:callSlug/ask/:kind" element={<CallAskPage />} />
        {/* One date's replay on Bunny: free for the call's window, then Member Plus (calls-replay-page.ts). */}
        <Route path="/calls/:callSlug/replay/:date" element={<CallReplayPage />} />
        <Route path="/webinars/:slug" element={<WebinarPage />} />
        <Route path="/webinars/:slug/thanks" element={<WebinarPage thanks />} />
        {/* Gated replay — email gate → signed Bunny iframe. A first-class URL
            like /thanks: the replay email links straight here, and it renders
            for QA/team preview without a registration on this device. */}
        <Route path="/webinars/:slug/replay" element={<WebinarReplayPage />} />
        {/* Lead-magnet library — browsable shelf across all magnets */}
        <Route path="/library" element={<MagnetLibraryPage />} />
        {/* Lead-magnet engine — generic by slug, zero per-book pages */}
        <Route path="/m/:slug" element={<MagnetPage />} />
        <Route path="/m/:slug/get" element={<MagnetAccessPage />} />
        {/* SMS opt-in (10DLC). Privacy/Terms are the canonical HubSpot pages
            at thesuccessfulbookkeeper.com — not duplicated here. */}
        <Route path="/sms" element={<SmsOptInPage />} />
        {/* Customer cancellation request form (docs/plans/2026-09-30-customer-
            cancellation-request-flow.md). TSB + BKOS on this one host; see
            CancelHostGate for the retired BKOS and PB taker hosts. */}
        {/* Website question forms (billing page first; ?embed=1 for an iframe).
            Answers land in the admin Inbox > Questions, mct_7427fc9e-e3f. */}
        <Route path="/questions/:kind" element={<QuestionFormPage />} />
        {/* The free-book page, one per book; a campaign is a link carrying
            ?from=<giver>&campaign=<key> (mct_55e916be-cda). /free-book/na|au stay
            as aliases of the TSB book, and with ?embed=1 still render the bare
            form the HubSpot pages iframe (mct_bdaba308-bee) until the redirects land. */}
        <Route path="/free-book/:book" element={<FreeBookPage />} />
        <Route path="/cancel" element={<CancelHostGate><CancelStartPage /></CancelHostGate>} />
        <Route path="/cancel/r/:token" element={<CancelHostGate><CancelRequestPage /></CancelHostGate>} />
        <Route path="/cancel/w/:token" element={<CancelHostGate><CancelWithdrawPage /></CancelHostGate>} />
        {/* Practice sale: the seller's one form (job mct_e446474b-67c). The token is the whole authority; TSB host, like /cancel. */}
        <Route path="/practice-sale/buy/:token" element={<CancelHostGate><PracticeSaleBuyPage /></CancelHostGate>} />
        <Route path="/practice-sale/:token" element={<CancelHostGate><PracticeSalePage /></CancelHostGate>} />
        <Route path="/bkos/more-clients/thanks" element={<BkosMoreClientsThanksPage />} />
        <Route path="/bkos/more-clients/:token" element={<BkosMoreClientsPage />} />
        <Route path="/bkos/approve/:token" element={<BkosApprovePage />} />
        <Route path="/credit/:token" element={<UpgradeCreditPage />} />
        {/* Book shipping address (book fulfilment plan 7.2, PR 8). The link in
            the "we need your address" email; the signed token is the only
            authority. TSB host only, like /cancel. */}
        <Route path="/books/address/:token" element={<BookAddressPage />} />
        {/* "Did your book arrive?" (thread mct_bb04f0ef-ceb): the Got it / Not
            yet buttons in the check-in email. One more tap records the answer. */}
        <Route path="/books/arrived/:token" element={<BookArrivedPage />} />
        <Route path="/bkos/rate/:token" element={<BkosRatePage />} />
        {/* Rate an event in the conference directory: rate first, confirm by email (mct_0bc60295-c2f). */}
        <Route path="/rate/confirm" element={<RateConfirmPage />} />
        <Route path="/rate/:slug" element={<RateEventPage />} />
        <Route path="/still-reading/:token" element={<StillReadingPage />} />
        {/* NA Summit promo emails' "not this year" confirmation (mct_ad39834d-8ea). Static; the opt-out happens in the smart link. */}
        <Route path="/summit-2026/not-this-year" element={<SummitNotThisYearPage />} />
        {/* A referrer's own share page (mct_ee41e1ee-080): reached only by its unguessable token. */}
        <Route path="/share/:token" element={<SharePage />} />
        {/* Testimonial capture (docs/plans/testimonial-capture.md §3). Same
            component for the personal-ask link and the standing brand form —
            the standing form resolves its brand server-side from the Origin
            host, never from a route param. */}
        <Route path="/testimonial" element={<TestimonialPage />} />
        <Route path="/testimonial/:token" element={<TestimonialPage />} />
        {/* Member check-in (mct_42ee6fc4-c7e; SOP sop-ee-member-checkins).
            The personal link from "A quick check-in on <program>" — the ?t=
            token is the whole identity, brand comes back in the payload. */}
        <Route path="/check-in" element={<CheckinPage />} />
        <Route path="*" element={<BrokenPage variant="404" />} />
      </Routes>
    </BrowserRouter>
  </React.StrictMode>
);
